import { useState } from 'react';
import styled from 'styled-components';
import { ButtonType, IconButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { colors, space } from '@ardoq/design-tokens';
import { SearchInput, FormSize } from '@ardoq/forms';
import {
  ComponentWithDropdown,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import type { DropdownItem, ComponentProps } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { FlexBox } from '@ardoq/layout';
import { type I18nLocale, t } from '@ardoq/i18n';
import type { Tab } from './Tabs';

type TabsDropdownProps<IdType extends string | number> = {
  i18nLocale: I18nLocale;
  tabs: Tab<IdType>[];
  activeTabId?: IdType;
  onTabChange: (tabId: IdType) => void;
  searchPlaceholder?: string;
  allTabsLabel?: string;
};

export const TabsDropdown = <IdType extends string | number>({
  i18nLocale,
  tabs,
  activeTabId,
  onTabChange,
  searchPlaceholder = t(
    i18nLocale,
    'integrations:tabsDropdown.searchPlaceholder',
    'Search for tabs'
  ),
  allTabsLabel = t(i18nLocale, 'integrations:tabsDropdown.allTabs', 'All tabs'),
}: TabsDropdownProps<IdType>) => {
  const [searchValue, setSearchValue] = useState('');

  const filteredTabs = tabs.filter(tab =>
    tab.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const dropdownOptions: DropdownItem[] = filteredTabs.map(tab => ({
    type: DropdownOptionType.OPTION,
    label: tab.label,
    onClick: () => onTabChange(tab.id),
    isSelected: tab.id === activeTabId,
    truncateLabel: true,
  }));

  if (tabs.length === 0) {
    return null;
  }

  const headerContent = (
    <SearchInput
      value={searchValue}
      onValueChange={setSearchValue}
      placeholder={searchPlaceholder}
      formSize={FormSize.FULL}
    />
  );

  return (
    <FlexBox>
      <ComponentWithDropdown
        options={dropdownOptions}
        alignMenuTo="right"
        Component={DropdownButton}
        headerContent={headerContent}
        isKeepOpen={true}
        dropdownSize={DropdownSize.M}
        componentProps={{
          label: allTabsLabel,
          iconName: IconName.MENU,
        }}
      />
    </FlexBox>
  );
};

type DropdownButtonProps = ComponentProps & {
  label: string;
  iconName: IconName;
};

const DropdownButton = ({
  ref,
  onMenuOpen,
  isMenuOpened,
  label,
  iconName,
}: DropdownButtonProps) => (
  <StyledDropdownButton
    ref={ref}
    label={label}
    onClick={onMenuOpen}
    iconName={iconName}
    data-is-menu-opened={isMenuOpened}
  />
);

const StyledDropdownButton = styled(IconButton).attrs({
  buttonType: ButtonType.GHOST,
})`
  height: 100%;
  border-radius: 0;
  padding: 0 ${space.medium};
  border-left: 1px solid ${colors.borderNeutral};
`;
